import { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, FormElement, Field } from "@progress/kendo-react-form";
import {
  FormInput,
  FormDropDownListById,
  FormCheckbox,
} from "../../forms/form-components";
import {
  getWidgetFontColors,
  fontSizes,
  decimalPlaces,
  thousandSeparators,
  widgetPageSizes,
} from "./utils";
import { List } from "../../components/list";
import { getDataTypeIcon, getExpressionIcon, isNumeric } from "../../lib/utils";
import { Input, Checkbox } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

export const TickerWidgetSettings = (props) => {
  const currentTheme = useSelector((state) => state.currentTheme);
  const fontColors = getWidgetFontColors(currentTheme);
  const {
    pageState,
    formState,
    onFieldValueChange,
    onTickerFieldsReorder,
    ...other
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  let dataSourceTitle = formState.formValues.settings.dataSourceTitle;
  if (!dataSourceTitle) {
    //the report is in view mode
    let dataSource = formState.dataSources.find(
      (e) => e.Id == formState.formValues.dataSourceId
    );
    if (!dataSource.IsImplicit) dataSourceTitle = dataSource.Title;
    else dataSourceTitle = formState.formValues.settings.schema[0].Title;
  }

  const listItemRenderer = (currentTheme) => (props, item, index) => {
    const {
      formRenderProps,
      onFieldCheck,
      onDragStart,
      onDragOver,
      onDrop,
      onDragStartHelper,
      onDragOverHelper,
      onDropHelper,
      ...other
    } = props;

    const handleOnDragStart = (e) => {
      let data = onDragStartHelper(e, item);
      if (onDragStart) onDragStart(e, data, formRenderProps);
    };

    const handleOnDragOver = (e) => {
      onDragOverHelper(e);

      if (onDragOver) onDragOver(e, formRenderProps);
    };

    const handleOnDrop = (e) => {
      let data = onDropHelper(e);
      if (onDrop) onDrop(e, data, formRenderProps);
    };

    return (
      <div
        key={item.id}
        className={`list-item`}
        data-drag-id={item.id}
        draggable="true"
        onDragStart={handleOnDragStart}
        onDragOver={handleOnDragOver}
        onDrop={handleOnDrop}
      >
        <div style={{ width: 28 }}>
          <Checkbox
            value={item.isVisible}
            onChange={(e) => onFieldCheck(item)}
          />
        </div>
        <div className="list-item-title">{item.title}</div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="ticker-settings">
        <div className="widget-settings-form-group" style={{ marginBottom: 6 }}>
          {formState.mode == 2 && (
            <div
              className="row"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <div style={{ fontWeight: 700 }}>Data Source:</div>
              <div style={{ marginLeft: 16 }}>{dataSourceTitle}</div>
            </div>
          )}
          <div className="header" style={{ marginTop: 12 }}>
            {t("extras.widget")} {t("extras.title")}
          </div>
          <div className="content">
            <div className="row">
              <Field
                id={"settings.title"}
                name={"settings.title"}
                component={FormInput}
                maxLength={255}
                style={{ width: "100%" }}
                onChange={(e) =>
                  onFieldValueChange({ id: "title", value: e.target.value })
                }
              />
            </div>
            <div className="row">
              <div style={{ width: 125 }}>
                <Field
                  id={"settings.titleFontSize"}
                  name={"settings.titleFontSize"}
                  label={t("extras.size")}
                  component={FormDropDownListById}
                  data={fontSizes}
                  dataItemKey="id"
                  textField="title"
                  style={{ marginTop: 2 }}
                  onChange={(e) =>
                    onFieldValueChange({ id: "titleFontSize", value: e.value })
                  }
                  // disabled={pageState.formValues.IsSystem}
                />
              </div>
              <div style={{ display: "flex", flex: 1 }}>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.titleFontIsBold"}
                    name={"settings.titleFontIsBold"}
                    label={t("extras.bold")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({
                        id: "titleFontIsBold",
                        value: e.value,
                      })
                    }
                  />
                </div>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.titleFontIsUnderline"}
                    name={"settings.titleFontIsUnderline"}
                    label={t("extras.underline")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({
                        id: "titleFontIsUnderline",
                        value: e.value,
                      })
                    }
                  />
                </div>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.titleFontIsItalic"}
                    name={"settings.titleFontIsItalic"}
                    label={t("extras.italic")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({
                        id: "titleFontIsItalic",
                        value: e.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div style={{ width: 125 }}>
                <Field
                  id={"settings.titleFontColor"}
                  name={"settings.titleFontColor"}
                  label={t("extras.color*")}
                  component={FormDropDownListById}
                  data={fontColors}
                  dataItemKey="id"
                  textField="title"
                  onChange={(e) =>
                    onFieldValueChange({ id: "titleFontColor", value: e.value })
                  }
                  // disabled={pageState.formValues.IsSystem}
                />
              </div>
              <div style={{ display: "flex", flex: 1 }}>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.reorderable"}
                    name={"settings.reorderable"}
                    label={t("extras.draggable")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({ id: "reorderable", value: e.value })
                    }
                  />
                </div>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.resizable"}
                    name={"settings.resizable"}
                    label={t("extras.resizable")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({ id: "resizable", value: e.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div style={{ width: 125 }}>
                <Field
                  id={"settings.maximumRows"}
                  name={"settings.maximumRows"}
                  label={t("extras.maxrows")}
                  type={"number"}
                  component={FormInput}
                  maxLength={7}
                  style={{ width: "100%" }}
                  onChange={(e) =>
                    onFieldValueChange({
                      id: "maximumRows",
                      value: isNumeric(e.target.value)
                        ? parseFloat(e.target.value)
                        : e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="widget-settings-form-group" style={{ marginBottom: 0 }}>
          <div className="header">{t("extras.ticker")}</div>
          <div className="content">
            <div className="row">
              <div style={{ width: 125 }}>
                <Field
                  id={"settings.fontSize"}
                  name={"settings.fontSize"}
                  label={t("extras.size")}
                  component={FormDropDownListById}
                  data={fontSizes}
                  dataItemKey="id"
                  textField="title"
                  onChange={(e) =>
                    onFieldValueChange({ id: "fontSize", value: e.value })
                  }
                  // disabled={pageState.formValues.IsSystem}
                />
              </div>
              <div style={{ display: "flex", flex: 1 }}>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.fontIsBold"}
                    name={"settings.fontIsBold"}
                    label={t("extras.bold")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({ id: "fontIsBold", value: e.value })
                    }
                  />
                </div>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.fontIsUnderline"}
                    name={"settings.fontIsUnderline"}
                    label={t("extras.underline")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({
                        id: "fontIsUnderline",
                        value: e.value,
                      })
                    }
                  />
                </div>
                <div style={{ marginLeft: 24 }}>
                  <Field
                    id={"settings.fontIsItalic"}
                    name={"settings.fontIsItalic"}
                    label={t("extras.italic")}
                    component={FormCheckbox}
                    onChange={(e) =>
                      onFieldValueChange({ id: "fontIsItalic", value: e.value })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div style={{ width: 125 }}>
                <Field
                  id={"settings.fontColor"}
                  name={"settings.fontColor"}
                  label={t("extras.color")}
                  component={FormDropDownListById}
                  data={fontColors}
                  dataItemKey="id"
                  textField="title"
                  onChange={(e) =>
                    onFieldValueChange({ id: "fontColor", value: e.value })
                  }
                  // disabled={pageState.formValues.IsSystem}
                />
              </div>
              <div style={{ flex: 1, marginLeft: 24 }}>
                <Field
                  id={"settings.backgroundColor"}
                  name={"settings.backgroundColor"}
                  label={t("extras.background")}
                  component={FormDropDownListById}
                  data={fontColors}
                  dataItemKey="id"
                  textField="title"
                  onChange={(e) =>
                    onFieldValueChange({
                      id: "backgroundColor",
                      value: e.value,
                    })
                  }
                  // disabled={pageState.formValues.IsSystem}
                />
              </div>
            </div>
            {formState.formValues.settings.fields &&
              formState.formValues.settings.fields.length > 0 && (
                <div className="row">
                  <List
                    className="ticker-fields" //onDrop={onTickerFieldsReorder}
                    items={formState.formValues.settings.fields}
                    onDrop={onTickerFieldsReorder}
                    itemRenderer={listItemRenderer(currentTheme)}
                    onFieldCheck={(field) => {
                      let fields = formState.formValues.settings.fields;
                      let checked = !field.isVisible;
                      onFieldValueChange({
                        id: "fields",
                        value: fields.map((e) => {
                          if (e.id == field.id)
                            return { ...e, isVisible: checked };
                          else return e;
                        }),
                      });
                    }}
                  />
                </div>
              )}
          </div>
        </div>
      </div>
      <style>
        {`
                        .widget-settings-form-group:first-child .k-form-field:first-of-type
                        {
                            width: 100%;
                        }
                       
                        .ticker-settings
                        {
                            display: flex;
                            flex-direction: column;
                            align-items: stretch;
                        }

                      
                    `}
      </style>
    </Fragment>
  );
};
